import React from "react";
import {
  Badge,
  Button,
  Card,
  MessageBar,
  MessageBarBody,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import {
  ArrowRight24Regular,
  Board24Regular,
  DocumentCheckmark24Regular,
  Folder24Regular,
  PeopleTeam24Regular,
  ShieldCheckmark24Regular,
  Sparkle24Regular,
} from "@fluentui/react-icons";
import { Page } from "../components/App";
import { useJob } from "../context/JobContext";
import { useRegistration } from "../context/RegistrationContext";
import { TenantUserRole } from "../types";
import { taskPaneBackground } from "../theme";
import { countAvailableWorkflows } from "./taskSelectionState";

type WorkflowRole = Exclude<TenantUserRole, "admin">;
type TaskTheme = "amber" | "blue" | "emerald" | "purple" | "rose";
type TaskIcon = React.ComponentType<{ className?: string }>;

interface TaskCardDefinition {
  actionLabel: string;
  category: string;
  description: string;
  icon: TaskIcon;
  isComingSoon?: boolean;
  theme: TaskTheme;
  title: string;
}

interface WorkflowDefinition extends TaskCardDefinition {
  role: WorkflowRole;
}

interface TaskCardProps {
  onSelect: () => void;
  task: TaskCardDefinition;
}

interface ThemeClasses {
  button: string;
  icon: string;
}

const useStyles = makeStyles({
  root: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    height: "88vh",
    overflow: "hidden",
    color: tokens.colorNeutralForeground1,
    ...taskPaneBackground,
  },
  header: {
    position: "relative",
    zIndex: 1,
    flexShrink: 0,
    ...shorthands.padding("20px", "20px", "20px"),
  },
  workspaceBadge: {
    marginBottom: "12px",
    ...shorthands.borderColor(tokens.colorNeutralStroke2),
    color: tokens.colorNeutralForeground1,
    backgroundColor: "rgba(255, 255, 255, 0.78)",
  },
  sparkleIcon: {
    color: "#2563eb",
  },
  title: {
    maxWidth: "520px",
    margin: 0,
    fontSize: "29px",
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: "1.08",
    letterSpacing: "-0.04em",
  },
  subtitle: {
    maxWidth: "520px",
    marginTop: "8px",
    marginBottom: 0,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
  },
  content: {
    position: "relative",
    zIndex: 1,
    minHeight: 0,
    flex: 1,
    overflowY: "auto",
    ...shorthands.padding("0", "20px", "24px"),
  },
  section: {
    marginTop: "24px",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "10px",
  },
  sectionTitle: {
    margin: 0,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: "0.11em",
    textTransform: "uppercase",
  },
  availableCount: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    fontVariantNumeric: "tabular-nums",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    ...shorthands.gap("12px"),
    "@media (min-width: 680px)": {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
  },
  card: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    cursor: "pointer",
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    ...shorthands.borderRadius("12px"),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
    transitionProperty: "border-color, box-shadow, transform",
    transitionDuration: "180ms",
    ":hover": {
      ...shorthands.borderColor(tokens.colorNeutralStroke1),
      boxShadow: tokens.shadow8,
      transform: "translateY(-2px)",
    },
    ":focus-within": {
      ...shorthands.borderColor(tokens.colorNeutralStroke1Pressed),
    },
  },
  cardHeader: {
    ...shorthands.padding("16px", "16px", "12px"),
  },
  cardTopRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    ...shorthands.gap("12px"),
    marginBottom: "16px",
  },
  iconContainer: {
    display: "flex",
    width: "40px",
    height: "40px",
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "center",
    ...shorthands.border("1px", "solid"),
    ...shorthands.borderRadius("12px"),
  },
  taskIcon: {
    width: "20px",
    height: "20px",
  },
  categoryBadge: {
    maxWidth: "calc(100% - 52px)",
    overflow: "hidden",
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderColor(tokens.colorNeutralStroke2),
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  comingSoonBadge: {
    color: "#be123c",
    backgroundColor: "#fff1f2",
    ...shorthands.borderColor("#fecdd3"),
  },
  cardTitle: {
    margin: 0,
    color: tokens.colorNeutralForeground1,
    fontSize: "17px",
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: "1.35",
    letterSpacing: "-0.015em",
  },
  cardDescription: {
    marginTop: "6px",
    marginBottom: 0,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase300,
    lineHeight: "20px",
  },
  cardFooter: {
    marginTop: "auto",
    ...shorthands.padding("0", "12px", "12px"),
  },
  actionButton: {
    width: "100%",
    height: "36px",
    justifyContent: "space-between",
    ...shorthands.border("1px", "solid"),
    ...shorthands.borderRadius("8px"),
    boxShadow: "none",
    fontSize: tokens.fontSizeBase300,
    transitionProperty: "background-color, border-color, color",
    transitionDuration: "150ms",
  },
  alert: {
    ...shorthands.border("1px", "solid", "#fcd34d"),
    ...shorthands.borderRadius("10px"),
    backgroundColor: "#fffbeb",
  },

  blueIcon: {
    color: "#1d4ed8",
    backgroundColor: "#eff6ff",
    ...shorthands.borderColor("#dbeafe"),
  },
  blueButton: {
    color: "#1e40af",
    backgroundColor: "#dbeafe",
    ...shorthands.borderColor("#bfdbfe"),
    ":hover": { color: "#1e3a8a", backgroundColor: "#bfdbfe" },
  },
  amberIcon: {
    color: "#b45309",
    backgroundColor: "#fffbeb",
    ...shorthands.borderColor("#fef3c7"),
  },
  amberButton: {
    color: "#78350f",
    backgroundColor: "#fef3c7",
    ...shorthands.borderColor("#fde68a"),
    ":hover": { color: "#451a03", backgroundColor: "#fde68a" },
  },
  roseIcon: {
    color: "#be123c",
    backgroundColor: "#fff1f2",
    ...shorthands.borderColor("#ffe4e6"),
  },
  roseButton: {
    color: "#9f1239",
    backgroundColor: "#ffe4e6",
    ...shorthands.borderColor("#fecdd3"),
    ":hover": { color: "#881337", backgroundColor: "#fecdd3" },
  },
  emeraldIcon: {
    color: "#047857",
    backgroundColor: "#ecfdf5",
    ...shorthands.borderColor("#d1fae5"),
  },
  emeraldButton: {
    color: "#065f46",
    backgroundColor: "#d1fae5",
    ...shorthands.borderColor("#a7f3d0"),
    ":hover": { color: "#064e3b", backgroundColor: "#a7f3d0" },
  },
  purpleIcon: {
    color: "#7e22ce",
    backgroundColor: "#faf5ff",
    ...shorthands.borderColor("#f3e8ff"),
  },
  purpleButton: {
    color: "#6b21a8",
    backgroundColor: "#f3e8ff",
    ...shorthands.borderColor("#e9d5ff"),
    ":hover": { color: "#581c87", backgroundColor: "#e9d5ff" },
  },
});

const WORKFLOWS: readonly WorkflowDefinition[] = [
  {
    actionLabel: "Start extraction",
    category: "Member data",
    description: "Collect pension scheme data with guided checks and traceable evidence.",
    icon: PeopleTeam24Regular,
    role: "member_data",
    theme: "blue",
    title: "Member Data Extraction",
  },
  {
    actionLabel: "Start Ben Spec",
    category: "Scheme documents",
    description: "Generate a full benefit specification report from scheme documents.",
    icon: ShieldCheckmark24Regular,
    role: "ben_spec",
    theme: "amber",
    title: "Benefit Specifications",
  },
  {
    actionLabel: "Check reports",
    category: "Quality control",
    description: "Validate output files and flag anomalies before sharing with clients.",
    icon: DocumentCheckmark24Regular,
    isComingSoon: true,
    role: "report_error_checking",
    theme: "rose",
    title: "Report Error Checking",
  },
];

const DASHBOARD_TASK: TaskCardDefinition = {
  actionLabel: "Open dashboard",
  category: "Jobs and reports",
  description: "Track active jobs, inspect results, and resume where you left off.",
  icon: Board24Regular,
  theme: "emerald",
  title: "Jobs Dashboard",
};

const SETUP_TASK: TaskCardDefinition = {
  actionLabel: "Run setup",
  category: "Workspace setup",
  description: "Create or verify the workspace used to store Sourcestone documents.",
  icon: Folder24Regular,
  theme: "purple",
  title: "Setup Sourcestone folder",
};

const ADMIN_STORAGE_TASK: TaskCardDefinition = {
  actionLabel: "Open admin setup",
  category: "Admin storage",
  description: "Register SharePoint sites and folders for authorised tenant users.",
  icon: ShieldCheckmark24Regular,
  theme: "amber",
  title: "SharePoint source setup",
};

const TaskCard: React.FC<TaskCardProps> = ({ onSelect, task }) => {
  const styles = useStyles();
  const Icon = task.icon;
  const themeClasses: Record<TaskTheme, ThemeClasses> = {
    amber: {
      button: styles.amberButton,
      icon: styles.amberIcon,
    },
    blue: {
      button: styles.blueButton,
      icon: styles.blueIcon,
    },
    emerald: {
      button: styles.emeraldButton,
      icon: styles.emeraldIcon,
    },
    purple: {
      button: styles.purpleButton,
      icon: styles.purpleIcon,
    },
    rose: {
      button: styles.roseButton,
      icon: styles.roseIcon,
    },
  };
  const theme = themeClasses[task.theme];

  return (
    <Card className={styles.card} onClick={onSelect}>
      <div className={styles.cardHeader}>
        <div className={styles.cardTopRow}>
          <div className={mergeClasses(styles.iconContainer, theme.icon)} aria-hidden>
            <Icon className={styles.taskIcon} />
          </div>
          <Badge
            appearance="outline"
            className={mergeClasses(
              styles.categoryBadge,
              task.isComingSoon && styles.comingSoonBadge
            )}
            shape="rounded"
            size="small"
          >
            {task.isComingSoon ? "Coming soon" : task.category}
          </Badge>
        </div>

        <h3 className={styles.cardTitle}>{task.title}</h3>
        <p className={styles.cardDescription}>{task.description}</p>
      </div>

      <div className={styles.cardFooter}>
        <Button
          appearance="secondary"
          className={mergeClasses(styles.actionButton, theme.button)}
          icon={<ArrowRight24Regular />}
          iconPosition="after"
          onClick={(event) => {
            event.stopPropagation();
            onSelect();
          }}
          type="button"
        >
          {task.actionLabel}
        </Button>
      </div>
    </Card>
  );
};

interface TaskSelectionPageProps {
  onNavigate: (page: Page) => void;
}

/** Displays role-gated workflows and shared workspace actions using Fluent UI. */
export const TaskSelectionPage: React.FC<TaskSelectionPageProps> = ({ onNavigate }) => {
  const styles = useStyles();
  const { dispatch } = useJob();
  const {
    state: { roles },
  } = useRegistration();
  const roleSet = React.useMemo(() => new Set(roles), [roles]);
  const visibleWorkflows = WORKFLOWS.filter(({ role }) => roleSet.has(role));
  const availableWorkflowCount = countAvailableWorkflows(visibleWorkflows);

  const handleMemberExtractionSelection = (): void => {
    dispatch({ type: "SET_JOB_TYPE", payload: "MEMBER_EXTRACTION" });
    onNavigate("fileSelection");
  };

  const handleBenefitSpecificationsSelection = (): void => {
    dispatch({ type: "SET_JOB_TYPE", payload: "BENEFIT_SPECIFICATION" });
    onNavigate("benSpecFileSelection");
  };

  const handleReportErrorCheckingSelection = (): void => {
    console.info("Report Error Checking workflow is coming soon.");
  };

  const workflowHandlers: Record<WorkflowRole, () => void> = {
    member_data: handleMemberExtractionSelection,
    ben_spec: handleBenefitSpecificationsSelection,
    report_error_checking: handleReportErrorCheckingSelection,
  };

  return (
    <main className={styles.root}>
      <header className={styles.header}>
        <Badge
          appearance="outline"
          className={styles.workspaceBadge}
          icon={<Sparkle24Regular className={styles.sparkleIcon} />}
          shape="rounded"
        >
          Sourcestone workspace
        </Badge>
        <h1 className={styles.title}>Choose your next move.</h1>
        <p className={styles.subtitle}>
          Start a new workflow or pick up existing work from your dashboard.
        </p>
      </header>

      <div className={styles.content}>
        <section aria-labelledby="workflow-heading">
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle} id="workflow-heading">
              Workflows
            </h2>
            {availableWorkflowCount > 0 && (
              <span className={styles.availableCount}>{availableWorkflowCount} available</span>
            )}
          </div>

          <div className={styles.grid}>
            {visibleWorkflows.map((workflow) => (
              <TaskCard
                key={workflow.role}
                onSelect={workflowHandlers[workflow.role]}
                task={workflow}
              />
            ))}

            {visibleWorkflows.length === 0 && (
              <MessageBar className={styles.alert} intent="warning">
                <MessageBarBody>
                  No Sourcestone workflows are enabled for your account. Contact your administrator
                  if you need access.
                </MessageBarBody>
              </MessageBar>
            )}
          </div>
        </section>

        <section className={styles.section} aria-labelledby="workspace-heading">
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle} id="workspace-heading">
              Workspace
            </h2>
          </div>

          <div className={styles.grid}>
            <TaskCard onSelect={() => onNavigate("dashboard")} task={DASHBOARD_TASK} />
            <TaskCard onSelect={() => onNavigate("setup")} task={SETUP_TASK} />
            {roleSet.has("admin") && (
              <TaskCard
                onSelect={() => onNavigate("adminStorageSetup")}
                task={ADMIN_STORAGE_TASK}
              />
            )}
          </div>
        </section>
      </div>
    </main>
  );
};
